import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { reviews, companyInfo } from '../data/mockData';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';

export const Reviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  return (
    <section id="reviews" className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Customer Reviews • Google Reviews
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Our Customers Say
          </h2>
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-green-500 text-green-500" />
              ))}
            </div>
            <span className="text-2xl font-bold text-gray-900">{companyInfo.rating.toFixed(1)}</span>
            <span className="text-gray-600">({companyInfo.totalReviews} Google Reviews)</span>
          </div>
        </div>

        {/* Featured Review Carousel */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="border-2 border-green-500/20 shadow-2xl">
            <CardContent className="p-12">
              <Quote className="w-16 h-16 text-green-500/30 mb-6" />
              <p className="text-2xl text-gray-700 mb-8 leading-relaxed italic">
                "{reviews[currentIndex].text}"
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Avatar className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600">
                    <AvatarFallback className="text-white font-bold text-xl">
                      {reviews[currentIndex].avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-bold text-xl text-gray-900">{reviews[currentIndex].name}</p>
                    <div className="flex items-center space-x-1">
                      {[...Array(reviews[currentIndex].rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-green-500 text-green-500" />
                      ))}
                      <span className="text-gray-500 text-sm ml-2">{reviews[currentIndex].date}</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={prevReview}
                    className="p-3 rounded-full bg-gray-100 hover:bg-green-500 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextReview}
                    className="p-3 rounded-full bg-gray-100 hover:bg-green-500 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* All Reviews Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {reviews.map((review, index) => (
            <Card
              key={review.id}
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500/30"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-green-500 text-green-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center space-x-3">
                  <Avatar className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600">
                    <AvatarFallback className="text-white font-bold">
                      {review.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-bold text-gray-900">{review.name}</p>
                    <p className="text-gray-500 text-sm">{review.date}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Review Platforms */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">Verified Google Reviews</p>
          <div className="flex justify-center space-x-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{companyInfo.rating.toFixed(1)}</div>
              <div className="flex items-center justify-center space-x-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-green-500 text-green-500" />
                ))}
              </div>
              <div className="text-sm text-gray-600">Google Reviews</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
